import { TestController } from "@sabyr-dev/mist_core/controller";
import {
	Attempt, ElibraryContentItem, Product, Question, Quiz
} from "@sabyr-dev/mist_elibrary/datasource";
import App from "@sabyr-dev/mist_core/app";
import { AnswerType } from "@sabyr-dev/mist_core/lib/userdata";
import * as math from "mathjs";
import { AcademyAnnotations } from "../lib/userdata";
import { AcademyApp } from "../app";

export class AcademyTestController extends TestController<Product, Attempt, Quiz, Question> {
	async answer(app: App<Product, Attempt, Quiz, Question>, answer: AnswerType): Promise<void> {
		super.answer(app, answer);
		const product = await app.model.metadata.getProductId();
		const attempt = await app.model.metadata.getAttempt();
		const current = app.current.get();
		const content = await app.content.get(current).catch(() => undefined);
		const meta = await app.content.meta(current);
		if (content) {
			const item = content?.id ?? meta.id;
			const correct = this.isCorrect(content, answer);
			const userdata = app.userdata.set(current, item, <AcademyAnnotations>{
				answer,
				status: answer ? (correct ? 'correct' : 'incorrect') : 'unanswered'
			});
			await app.model.item.setUserData(product, attempt, item, userdata);
		}
	}

	isCorrect(content: ElibraryContentItem, answer: AnswerType): boolean {
		switch (content.type) {
			case "multiplechoice":
				return answer == content.multiplechoice.answers
					.filter(ans => ans.correct)[0]
					?.name;
			case "multiplecorrect":
				return JSON.stringify(answer) == JSON.stringify(content.multiplecorrect.answers
					.filter(ans => ans.correct)
					.map(ans => ans.name));
			case "constructedresponse":
				return !!answer.length;
			case "numeric":
				if (answer && content.numeric.answers.minimum && content.numeric.answers.maximum) {
					return this.compareNumeric(
						content.numeric.answers.minimum.text,
						content.numeric.answers.minimum.value,
						answer as string
					) <= 0 && this.compareNumeric(
						content.numeric.answers.maximum.text,
						content.numeric.answers.maximum.value,
						answer as string
					) >= 0;
				} else if (answer && content.numeric.answers.number) {
					return this.compareNumeric(
						content.numeric.answers.number.text,
						content.numeric.answers.number.value,
						answer as string
					) === 0;
				}
				return false;
			case "cloze":
			case "matching":
			case "sorting":
				return false;
			default:
				return false;
		}
	}

	compareNumeric(referenceText: string, referenceValue: number, compareText: string): number | math.BigNumber | math.Fraction | math.MathCollection {
		try {
			return math.compare(math.evaluate(referenceText), math.evaluate(compareText));
		} catch (ex) {
			try {
				return math.compare(referenceValue, math.evaluate(compareText));
			} catch (ex) {
				try {
					return math.compare(referenceValue,
						compareText.replace(/[^\-.0-9\/]/g, '')
					);
				} catch (ex) {
					console.warn("Could not parse", compareText);
				}
			}
		}
	}

	// @ts-ignore
	finish(app: AcademyApp): void {
		app.view.item.displayFinishing('loading');
		Promise.all([
			app.model.metadata.getProductId(),
			app.model.metadata.getAttempt(),
			app.timer.total(),
		]).then((record: [Product, Attempt, number]) => ({
			product: record[0],
			attempt: record[1],
			total_time: record[2]
		})).then((record: { product: Product, attempt: Attempt, total_time: number }) =>
			app.model.item.setFinished(record.product, record.attempt, record.total_time)
		).then((url: string) => {
			app.view.item.displayResults(url);
		}).catch(() => {
			app.view.item.displayFinishing('error');
		});
	}
}
